/* eslint-disable no-shadow */
/* eslint-disable camelcase */
import React, { useState, useMemo, useContext, useEffect } from 'react';
import getCurrencySymbol from 'currency-symbol-map';
import { useSelector } from 'react-redux';
import { isEmpty, isEqual } from 'lodash';
import PropTypes from 'prop-types';

import { getSelectionData, stockInfo, selectionInfo, getAvailableOptions } from '../helper';
import { FormContext } from '../../../../shared/contexts/form-context';
import { InventoryProductPropType } from '../../../ctrl_proptype';
import OptionsSelectionModal from './modal/options-selector';
import SelectionArea from './selection-area';
import ListProduct from './list_product';
import OutOfStock from './out_of_stock';
import CheckBox from './checkbox';
import {
  OutOfStockWrapper,
  GridImageWrapper,
  GridProduct,
  GridDetail,
  Image,
} from '../inventory.styled';

const Product = ({ updateProductSelection, ctrlValidationError, outOfStock, formWidth, product, view }) => {
  const price = product.price ? `${getCurrencySymbol(product.payment_currency || 'USD')}${product.price.toLocaleString()}` : '';
  const image = product.image ? product.image : 'https://storage.googleapis.com/fplsblog/default-placeholder.svg';
  const { editingResponse: isEditingResponse } = useSelector((state) => state.form);
  const { previewViewPort } = useSelector((state) => state.builder.present);
  const { inCustomise, inBuilder, inForm } = useContext(FormContext);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [selection, setSelection] = useState(product.selection);
  const availableOptions = getAvailableOptions(product);
  const [errorMessage, setErrorMessage] = useState('');
  const updatedProduct = { ...product, selection };
  const isOutOfStock = stockInfo(product);

  // Product selectiom updaters
  const { quantity: selectionQuantity, combination: selectionCombination } = getSelectionData(updatedProduct);
  const { isUnavailable, isSelected } = selectionInfo(updatedProduct);
  
  const outOfStockMessage = isOutOfStock ? 'Out of stock' : '';

  const options = useMemo(() => {
    const newOptions = product.options.map(({ label, values }) => {
      const options = values.map((value, index) => {
        const option = availableOptions.find((option) => option.label === label);
        const item = { key: `${value}-${index}`, text: value, value };

        if (product.options.length > 1 && option && !option.values.includes(value)) {
          item.label = { color: 'red', empty: true, circular: true };
        }

        return item;
      });

      return { label, options };
    });

    return newOptions.filter(({ label, options }) => label && options.length);
  }, [product.options, availableOptions]);

  const productHasSingleOrNoOptions = [0, 1].includes(options.length);
  const isSingleOptionProduct = options.length === 1;

  const selectionErrors = {
    maximumQuantity: selection.quantity && selection.quantity >= selectionQuantity,
    isRequired: options.length >= 1 && !isSelected,
    isOutOfStock: isSelected && !selectionQuantity,
    isUnavailable,
  };

  const isSelectionError = Object.values(selectionErrors).includes(true);

  const openOptionsModal = () => inForm && setShowOptionsModal(true);
  const closeOptionsModal = () => setShowOptionsModal(false);

  const getOptionValue = (label) => selection.combination[label];

  const handleOptionsChange = (label, value) => {
    const newSelection = { ...selection };
    newSelection.combination[label] = value;
    
    const { variant_key } = getSelectionData({ ...product, selection: newSelection });
    if (variant_key) newSelection.variant_key = Number(variant_key);

    setSelection(newSelection);
  };

  const toggleErrorMessage = (message) => {
    setErrorMessage(message);
    const optionErrorTimeout = setTimeout(() => {
      setErrorMessage('');
      clearTimeout(optionErrorTimeout);
    }, 3000);
  };

  const updateQuantity = (quantity) => {
    const newSelection = { ...selection };
    newSelection.quantity = quantity;
    setSelection(newSelection);
  };

  const resetOverSelectedStocks = () => {
    if ((selectionQuantity && selection.quantity > selectionQuantity) || !selectionQuantity || isUnavailable) {
      updateQuantity(selectionQuantity)
    }
  };

  // Order is important here
  const showErrorMessage = () => {
    if (selectionErrors.isUnavailable) return toggleErrorMessage('Selected variant is unavailabe!');
    if (selectionErrors.isOutOfStock) return toggleErrorMessage('Selected variant is out of stock!');
    if (selectionErrors.maximumQuantity) return toggleErrorMessage('Maximum selection reached!');
    if (selectionErrors.isRequired) return toggleErrorMessage('Select an option first!');
  };

  const incrementQuantity = () => {
    if (isSelectionError) return showErrorMessage();
    updateQuantity(selection.quantity + 1);
  };

  const decrementQuantity = () => {
    if (selection.quantity <= 0) return;
    updateQuantity(selection.quantity - 1);
  };

  const handleQuantity = (e) => {
    const value = Math.abs(+e.target.value);
    
    if (isSelectionError && (!selectionErrors.maximumQuantity || value >= selection.quantity)) {
      return showErrorMessage();
    }

    updateQuantity(value);
  };

  const selectProduct = () => {
    if (selection.quantity) return updateQuantity(0);

    if (!options.length || (options.length && isSelected && !isUnavailable)) {
      return updateQuantity(1);
    }

    if (isSelectionError) return showErrorMessage();
  };

  useEffect(() => {
    // Update product selection using a clean version of user selected variant combination.
    updateProductSelection({ id: product.id, selection, combination: selectionCombination });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection.quantity]);

  useEffect(() => {
    resetOverSelectedStocks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection.quantity, selectionQuantity]);

  if (outOfStock === 'hide_product' && isOutOfStock) return null;
  
  return (
    <>
      {view === 'grid' ? (
        <GridProduct isMobileBuilderPreview={previewViewPort === 'mobile'} data-testid={product?.id}>
          <GridImageWrapper>
            <CheckBox
              disabled={!inForm || isOutOfStock || isEditingResponse}
              updateProduct={selectProduct}
              value={!!selection.quantity}
              prop="quantity"
            />

            {(errorMessage || ctrlValidationError || outOfStockMessage) && (
              <OutOfStockWrapper>
                <OutOfStock ctrlValidationError={ctrlValidationError || outOfStockMessage || errorMessage} />
              </OutOfStockWrapper>
            )}

            <Image src={image} alt="product" />
          </GridImageWrapper>

          <GridDetail>
            <div className="top">
              <div className="title" title={product.product_title}>
                <span>{product.product_title}</span>
              </div>

              {product.description && (
                <div className="description">
                  <p title={product.description}>{product.description}</p>
                </div>
              )}

              <SelectionArea
                productHasSingleOrNoOptions={productHasSingleOrNoOptions}
                isSingleOptionProduct={isSingleOptionProduct}
                handleOptionsChange={handleOptionsChange}
                decrementQuantity={decrementQuantity}
                incrementQuantity={incrementQuantity}
                isEditingResponse={isEditingResponse}
                openOptionsModal={openOptionsModal}
                handleQuantity={handleQuantity}
                getOptionValue={getOptionValue}
                quantity={selection.quantity}
                isOutOfStock={isOutOfStock}
                options={options}
                inForm={inForm}
                price={price}
              />
            </div>
            
            {price && (
              <div className="price">
                <span>{price}</span>
              </div>
            )}
          </GridDetail>
        </GridProduct>
      ) : (
        <ListProduct
          ctrlValidationError={ctrlValidationError || outOfStockMessage}
          productHasSingleOrNoOptions={productHasSingleOrNoOptions}
          isMobileBuilderPreview={previewViewPort === 'mobile'}
          isSingleOptionProduct={isSingleOptionProduct}
          handleOptionsChange={handleOptionsChange}
          incrementQuantity={incrementQuantity}
          decrementQuantity={decrementQuantity}
          isEditingResponse={isEditingResponse}
          openOptionsModal={openOptionsModal}
          handleQuantity={handleQuantity}
          getOptionValue={getOptionValue}
          selectProduct={selectProduct}
          quantity={selection.quantity}
          errorMessage={errorMessage}
          isOutOfStock={isOutOfStock}
          inCustomise={inCustomise}
          data-testid={product.id}
          inBuilder={inBuilder}
          formWidth={formWidth}
          product={product}
          options={options}
          inForm={inForm}
          price={price}
          image={image}
        />
      )}

      <OptionsSelectionModal
        disableQuantityInput={!inForm || isOutOfStock || isEditingResponse}
        disableIncrement={!inForm || isOutOfStock || isEditingResponse}
        disableOptionSelection={!inForm || isEditingResponse}
        disableDecrement={!inForm || isEditingResponse}
        handleOptionsChange={handleOptionsChange}
        decrementQuantity={decrementQuantity}
        incrementQuantity={incrementQuantity}
        handleQuantity={handleQuantity}
        getOptionValue={getOptionValue}
        quantity={selection.quantity}
        errorMessage={errorMessage}
        onClose={closeOptionsModal}
        isOpen={showOptionsModal}
        options={options}
      />
    </>
  );
};

Product.propTypes = {
  formWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  product: PropTypes.shape(InventoryProductPropType).isRequired,
  updateProductSelection: PropTypes.func.isRequired,
  outOfStock: PropTypes.string.isRequired,
  ctrlValidationError: PropTypes.string,
  view: PropTypes.string.isRequired,
};

Product.defaultProps = {
  ctrlValidationError: null,
};

export default Product;
